import { ShieldCheck, Award, Users, Calendar, Globe2 } from 'lucide-react';

/**
 * TrustSignalStrip — compact, scannable row of E-E-A-T trust signals.
 * Recommended placement: right after the hero on high-intent pages
 * (Home, country hubs, visa pages, study-abroad hub).
 *
 * Six signals, all verifiable on-page or via the MEA registry:
 *   1. MEA license number     ─ Authoritativeness
 *   2. Year founded           ─ Experience
 *   3. Total placements       ─ Experience
 *   4. Countries served       ─ Expertise
 *   5. Visa success rate      ─ Trust
 *   6. Free profile review    ─ Risk-reversal CTA
 *
 * NOTE: All numbers come from a single source of truth (10,000+ placements,
 * 2015 founding, 50+ countries). Update here AND in the verified data file
 * if anything changes — never inflate numbers in one place only.
 */

const SIGNALS = [
  {
    icon: ShieldCheck,
    label: 'MEA Approved',
    value: 'RC: B-0762',
    sub: 'Government of India',
  },
  {
    icon: Calendar,
    label: 'Founded',
    value: '2015',
    sub: '10+ years guidance',
  },
  {
    icon: Users,
    label: 'Placements',
    value: '10,000+',
    sub: 'Verified by MEA',
  },
  {
    icon: Globe2,
    label: 'Destinations',
    value: '50+',
    sub: 'Countries served',
  },
  {
    icon: Award,
    label: 'Visa Success',
    value: '94%',
    sub: 'Approval rate',
  },
];

const TrustSignalStrip = ({ variant = 'default', className = '' }) => {
  const isCompact = variant === 'compact';

  return (
    <section
      className={`py-8 sm:py-10 bg-white border-y border-slate-200 ${className}`}
      aria-labelledby="trust-signal-heading"
      data-testid="trust-signal-strip"
    >
      <h2 id="trust-signal-heading" className="sr-only">
        Verified credentials and track record
      </h2>
      <div className="container mx-auto px-4 sm:px-6">
        <ul className={`grid ${
          isCompact ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5'
        } gap-4 md:gap-6 max-w-6xl mx-auto`}>
          {SIGNALS.map((s) => (
            <li
              key={s.label}
              className="flex flex-col items-center text-center px-2"
              data-testid={`trust-signal-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-10 h-10 rounded-full bg-zafco-blue/10 flex items-center justify-center mb-2 ring-1 ring-zafco-blue/10">
                <s.icon className="w-5 h-5 text-zafco-blue" aria-hidden="true" />
              </div>
              <div className="text-lg sm:text-xl font-bold text-zafco-blue leading-tight">
                {s.value}
              </div>
              <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-gray-600 mt-0.5">
                {s.label}
              </div>
              <div className="text-[10.5px] text-gray-600 mt-0.5">{s.sub}</div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default TrustSignalStrip;
